import { AccountNameEditor } from "../components/AccountNameEditor";
import { ProfessionalFields } from "./worker-profile/ProfessionalFields";
import { VerificationQueuePanel } from "./worker-profile/VerificationQueuePanel";
import { PortfolioEditor } from "./worker-profile/PortfolioEditor";
import { AvailabilityEditor } from "./worker-profile/AvailabilityEditor";
import { BIO_CHARACTER_LIMIT, ONBOARDING_FIELDS, COMPLETENESS_LABELS, EDUCATION_LEVELS, educationDrafts, experienceDrafts, type EducationDraft, type ExperienceDraft, type FieldErrors, type OnboardingField } from "./worker-profile/profileDrafts";
import { confirmJobseekerEdit } from "../lib/jobseekerEdit";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Loader2, CheckCircle2, AlertCircle, Lock } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { JobseekerDiscoveryControl } from "../components/JobseekerDiscoveryControl";
import { useI18n } from "../lib/i18n";
import { LocationPin } from "../components/LocationPin";
import { SmartSelect } from "../components/SmartInputs";
import { YEARS_OPTIONS, NATIONALITIES } from "../data/postTaxonomy";
import { MobileSettingsBreadcrumb } from "../components/MobileSettingsBreadcrumb";
import "./SettingsLinkedPages.mobile.css";
import "./WorkerProfile.mobile.css";
import "./WorkerProfile.desktop.css";

const GENDERS = ["", "male", "female", "prefer_not_to_say"];
export default function WorkerProfile() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const search = useSearch();
  const requestedField = new URLSearchParams(search).get("field");
  const focusedTarget = useRef("");
  const [section, setSection] = useState(() => new URLSearchParams(window.location.search).get("section") === "availability" ? "availability" : "professional");
  const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 640px)");
    const update = () => setDesktop(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const [data, setData] = useState<any>(null);
  const [f, setF] = useState<any>({});
  const [saveAction, setSaveAction] = useState<"profile" | "onboarding" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [requirementsVersion, setRequirementsVersion] = useState(0);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => {
    if (!user) return;
    api.workerProfile().then((d) => {
      setData(d);
      setF({
        headline: d.profile.headline || "", educationEntries: educationDrafts(d.profile), experienceEntries: experienceDrafts(d.profile), bio: d.profile.bio || "", city: d.profile.city || "", province: d.profile.province || "",
        primaryRole: d.profile.primaryRole || "", skills: (d.profile.skills || []).join(", "),
        yearsExperience: d.profile.yearsExperience ?? "", expectedRate: d.profile.expectedRate ?? "",
        birthdate: d.profile.birthdate || "", gender: d.profile.gender || "", nationality: d.profile.nationality || "",
      });
    }).catch(() => setData(null));
  }, [user]);

  useEffect(() => {
    const targets:Record<string,{section:string;id:string}>={name:{section:'personal',id:'profile-first-name'},headlineOrPrimaryRole:{section:'professional',id:'professional-bio'},bio:{section:'professional',id:'worker-about'},city:{section:'professional',id:'worker-city-field'},skills:{section:'professional',id:'worker-skills'},yearsExperience:{section:'professional',id:'worker-years-experience'}};
    const target=requestedField?targets[requestedField]:undefined;
    const key=user?.id+':'+search+':'+desktop;
    if(!data||!target||focusedTarget.current===key)return;
    if(desktop&&section!==target.section){setSection(target.section);return}
    const frame=requestAnimationFrame(()=>{const root=document.getElementById(target.id);const control=root?.matches('input,textarea,button')?root:root?.querySelector<HTMLElement>('input:not(:disabled),textarea:not(:disabled),button:not(:disabled)');if(control){root?.scrollIntoView({block:'center',behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});control.focus({preventScroll:true});focusedTarget.current=key}});
    return()=>cancelAnimationFrame(frame);
  },[data,requestedField,search,desktop,section,user?.id]);

  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;
  const isWorker = (user.capabilities || []).some((c) => c === "find_work" || c === "offer_service");
  if (!isWorker) return <div className="container-x py-10 max-w-2xl"><div className="card p-5 text-sm text-navy-700">Enable Find Work or Offer a Service to set up a jobseeker profile.</div></div>;
  if (!data) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  const locked = data.personalLock?.locked;
  const lockUntil = data.personalLock?.until ? String(data.personalLock.until).slice(0, 10) : null;
  const min = data.minDailyRate || 695;
  // The API owns the experience limit; older responses leave the upper-bound check to the server.
  const maxYearsExperience: number | undefined = Number.isSafeInteger(data.maxYearsExperience) && data.maxYearsExperience >= 0 ? data.maxYearsExperience : undefined;
  const experienceOptions = YEARS_OPTIONS.filter((year) => maxYearsExperience === undefined || Number(year) <= maxYearsExperience);
  const busy = saveAction !== null;

  function payload() {
    const drafts: EducationDraft[] = Array.isArray(f.educationEntries) ? f.educationEntries : [];
    const educationEntries = drafts.map((entry) => ({
      level: entry.level,
      school: String(entry.school || "").trim(),
      degree: String(entry.degree || "").trim() || undefined,
      yearGraduated: entry.yearGraduated === "" ? null : Number(entry.yearGraduated),
    })).filter((entry) => entry.school || entry.degree || entry.yearGraduated != null);
    const education = educationEntries.map((entry) => [
      EDUCATION_LEVELS.find((level) => level.value === entry.level)?.label,
      entry.degree,
      entry.school,
      entry.yearGraduated == null ? undefined : `Graduated ${entry.yearGraduated}`,
    ].filter(Boolean).join(" · ")).join("\n");
    const experienceEntries = (Array.isArray(f.experienceEntries) ? f.experienceEntries as ExperienceDraft[] : []).map((entry) => ({
      role: String(entry.role || "").trim(),
      yearsExperience: entry.yearsExperience === "" ? null : Number(entry.yearsExperience),
    })).filter((entry): entry is { role: string; yearsExperience: number } => entry.role.length > 0 && entry.yearsExperience != null && Number.isFinite(entry.yearsExperience));
    return {
      headline: f.headline.trim(), education, educationEntries, experienceEntries, bio: f.bio.trim(), city: f.city.trim(), province: f.province.trim(),
      primaryRole: f.primaryRole.trim(),
      skills: String(f.skills || "").split(",").map((s: string) => s.trim()).filter(Boolean),
      yearsExperience: f.yearsExperience === "" ? null : Number(f.yearsExperience),
      expectedRate: f.expectedRate === "" ? null : Number(f.expectedRate),
      birthdate: f.birthdate || undefined, gender: f.gender || undefined, nationality: f.nationality.trim() || undefined,
    };
  }
  function clearFieldError(field: OnboardingField) {
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  }
  function inlineError(field: OnboardingField) {
    return fieldErrors[field] && <p id={`worker-${field}-error`} role="alert" className="text-xs text-brand-red mt-1">{fieldErrors[field]}</p>;
  }
  // [profile.editing-and-progress](../../../docs/product/profiles-and-verification.md#profile-editing-and-progress): required setup fields block onboarding only; partial saves still allow blanks.
  // Bio's length limit applies to both actions, and inline feedback focuses the first invalid field.
  function validateSetup(profile: ReturnType<typeof payload>, onboard: boolean) {
    const errors: FieldErrors = {};
    if (profile.headline.length > BIO_CHARACTER_LIMIT) errors.headline = `Keep Bio to ${BIO_CHARACTER_LIMIT} characters or fewer.`;
    if (onboard) {
      if (profile.headline.length < 3) errors.headline = "Add a short Bio of at least 3 characters.";
      if (profile.city.length < 2 || profile.city.length > 100) errors.city = "Choose a location or enter a city of 2–100 characters.";
      if (profile.primaryRole.length < 2 || profile.primaryRole.length > 80) errors.primaryRole = "Choose or enter a primary role of 2–80 characters.";
      if (profile.yearsExperience == null || !Number.isInteger(profile.yearsExperience) || profile.yearsExperience < 0 || (maxYearsExperience !== undefined && profile.yearsExperience > maxYearsExperience)) {
        errors.yearsExperience = maxYearsExperience === undefined
          ? "Enter a whole number of years, 0 or more. Use 0 if you are just starting."
          : `Enter years of experience from 0 to ${maxYearsExperience}. Use 0 if you are just starting.`;
      }
    }
    setFieldErrors(errors);
    const firstMissing = ONBOARDING_FIELDS.find((field) => errors[field]);
    if (firstMissing && desktop) setSection("professional");
    if (firstMissing) requestAnimationFrame(() => {
      const field = document.getElementById(`worker-${firstMissing}-field`);
      field?.scrollIntoView({ behavior: "smooth", block: "center" });
      field?.querySelector<HTMLInputElement | HTMLSelectElement>("input:not(:disabled), select:not(:disabled)")?.focus({ preventScroll: true });
    });
    return !firstMissing;
  }
  async function save(onboard: boolean) {
    setErr(null); setOkMsg(null);
    const profile = payload();
    if (!validateSetup(profile, onboard)) return;
    const startedAt = Date.now();
    setSaveAction(onboard ? "onboarding" : "profile");
    try {
      const d = await confirmJobseekerEdit((confirmJobseekerDisable) => onboard
        ? api.completeWorkerOnboarding({ ...profile, confirmJobseekerDisable })
        : api.updateWorkerProfile({ ...profile, confirmJobseekerDisable }));
      if (!d) return;
      setData(d);
      setRequirementsVersion((version) => version + 1);
      setF((current: Record<string, unknown>) => ({
        ...current,
        educationEntries: educationDrafts(d.profile),
        experienceEntries: experienceDrafts(d.profile),
      }));
      setOkMsg(onboard ? "Onboarding complete — your profile is set up." : "Profile saved.");
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Could not save.");
    } finally {
      // Keep the spinner visible briefly so a fast request still gives clear feedback.
      const remaining = Math.max(0, 300 - (Date.now() - startedAt));
      if (remaining > 0) await new Promise((resolve) => window.setTimeout(resolve, remaining));
      setSaveAction(null);
    }
  }

  const sections = ["professional", "background", "personal", "portfolio", "availability"];
  const sectionLabels = ["Professional", "Background", "Personal", "Portfolio", "Availability"];
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  const skills = String(f.skills || "").split(",").map((skill) => skill.trim()).filter(Boolean);

  return (
    <div data-section={section} className="worker-profile-page enhanced-form-fields container-x py-6 max-w-2xl">
      <MobileSettingsBreadcrumb currentLabel={t("settings.editPublicProfile")} showSettings={false} />
      {desktop && <div className="worker-desktop-top"><nav aria-label="Breadcrumb"><Link href="/dashboard">Account</Link><span aria-hidden="true">›</span><h1 aria-current="page">Jobseeker profile</h1></nav><Link className="btn-outline" href={`/u/${user.id}`}>Public profile ↗</Link></div>}
      <div className="worker-desktop-layout"><div className="worker-desktop-editor">
      {desktop && <nav className="worker-desktop-tabs" role="tablist" aria-label="Profile sections">{sections.map((key, index) => <button type="button" key={key} id={`worker-tab-${key}`} role="tab" aria-selected={section === key} aria-controls="worker-desktop-fields" tabIndex={section === key ? 0 : -1} onClick={() => setSection(key)} onKeyDown={(event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault(); const next = event.key === "Home" ? 0 : event.key === "End" ? sections.length - 1 : (index + (event.key === "ArrowRight" ? 1 : -1) + sections.length) % sections.length;
        setSection(sections[next]); document.getElementById(`worker-tab-${sections[next]}`)?.focus();
      }}>{sectionLabels[index]}</button>)}</nav>}
      <div id="worker-desktop-fields" role={desktop ? "tabpanel" : undefined} aria-labelledby={desktop ? `worker-tab-${section}` : undefined}>
      {/* Completeness meter */}
      <div className="worker-profile-completeness card p-4 mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-semibold text-navy">{t("dashboard.completion.title", "Profile setup")}</span>
          <span className="font-bold text-navy">{data.completeness}%</span>
        </div>
        <div className="h-2 rounded-full bg-navy-50 overflow-hidden" role="progressbar" aria-label={t("dashboard.completion.title", "Profile setup")} aria-valuenow={data.completeness} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-brand-green transition-all" style={{ width: `${data.completeness}%` }} />
        </div>
        <p className="text-xs text-mut mt-2">{t("profile.setupExplanation", "Blank optional fields do not reduce this score. Jobseeker requirements are checked separately.")}</p>
        {data.missing?.length > 0 && <p className="text-xs text-navy mt-2">Still missing: {data.missing.map((field: string) => COMPLETENESS_LABELS[field] || field).join(", ")}.</p>}
        {!data.onboarded && <p className="text-xs text-mut mt-2">Save your progress anytime, then complete onboarding when the required fields are ready.</p>}
        {user.capabilities.includes("find_work") && <div className="border-t border-line mt-4 pt-1">
          <JobseekerDiscoveryControl key={`${user.id}:${requirementsVersion}`} userId={user.id} requirementsOnly />
        </div>}
      </div>

      <ProfessionalFields {...{ f, setF, desktop, section, fieldErrors, clearFieldError, inlineError, experienceOptions, maxYearsExperience, min }} />

      <div hidden={desktop&&section!=="personal"} className="mb-4"><AccountNameEditor key={user.id}/></div>
      {/* Personal (lockable) */}
      <div className="worker-profile-section worker-profile-personal card p-5 mb-4 space-y-3">
        <h3 className="font-bold text-navy flex items-center gap-2">Personal {locked && <span className="chip bg-navy-50 text-navy-600 text-xs"><Lock className="w-3 h-3" />Locked until {lockUntil}</span>}</h3>
        {locked && <p className="text-xs text-mut">To protect trust, personal details are locked for 30 days after onboarding. Contact support to change them sooner.</p>}
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="label">Birthdate</label><input className="field" type="date" disabled={locked} value={f.birthdate} onChange={(e) => setF({ ...f, birthdate: e.target.value })} /></div>
          <div><label className="label">Gender</label><select className="field" disabled={locked} value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}>{GENDERS.map((g) => <option key={g} value={g}>{g ? g.replace(/_/g, " ") : "—"}</option>)}</select></div>
          <div><label className="label">Nationality</label>
            {locked
              ? <input className="field" disabled value={f.nationality} placeholder="e.g. Filipino" />
              : <SmartSelect value={f.nationality} onChange={(v) => setF({ ...f, nationality: v })} options={NATIONALITIES.filter((n) => n !== "Other")} placeholder="Select or type — e.g. Filipino" />}
          </div>
        </div>
      </div>

      <VerificationQueuePanel workerVerified={!!user.workerVerified} />
      <PortfolioEditor />
      <AvailabilityEditor key={`${user.id}:${user.activeContext || "personal"}`} />

      </div>
      {err && <p role="alert" className="text-sm text-brand-red flex items-center gap-1 mt-4"><AlertCircle className="w-4 h-4" />{err}</p>}
      {okMsg && <p role="status" className="text-sm text-brand-green flex items-center gap-1 mt-4"><CheckCircle2 className="w-4 h-4" />{okMsg}</p>}
      <div className="worker-profile-actions mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm text-mut" role="status">
          <CheckCircle2 className="w-4 h-4 text-brand-green" />
          Portfolio and availability save automatically.
        </div>
        {!data.onboarded && <button type="button" onClick={() => save(true)} disabled={busy} aria-busy={saveAction === "onboarding"} className="btn-outline self-start disabled:opacity-60">
          {saveAction === "onboarding" ? "Completing…" : "Complete onboarding"}
        </button>}
        <button type="button" onClick={() => save(false)} disabled={busy} aria-busy={saveAction === "profile"} className="btn-red self-start disabled:opacity-60">
          {saveAction === "profile" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {saveAction === "profile" ? "Saving…" : "Save profile"}
        </button>
      </div>
      </div>
      {desktop && <aside className="worker-desktop-preview card"><div className="worker-preview-cover" /><div className="worker-preview-body"><div className="worker-preview-avatar">{name.split(/\s+/).map((part) => part[0]).slice(0, 2).join("")}</div><p className="worker-preview-label">Public profile preview</p><h2>{name}</h2><p>{f.headline || "Add a short bio"}</p><div className="worker-preview-location"><LocationPin />{f.city || "Add your location"}{f.yearsExperience !== "" && <span> · {f.yearsExperience} years of experience</span>}</div><div className="worker-preview-skills">{skills.map((skill) => <span key={skill}>{skill}</span>)}</div><div className="worker-preview-about"><h3>About</h3><p>{f.bio || "Tell clients about your experience and specialties."}</p></div>{f.expectedRate !== "" && <div className="worker-preview-rate"><span>Expected daily rate</span><strong>₱{Number(f.expectedRate).toLocaleString("en-PH")} / day</strong></div>}<p className="worker-preview-note">Personal information is edited separately from this public preview.</p></div></aside>}
      </div>
    </div>
  );
}
